/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { call, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Channel,
  Asset,
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  Time,
  Configs,
  PlaybackLicenseResult,
  PlaybackContextApi,
  PlaybackAssetType,
  TimeUtils,
  Constants,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import {
  ActionTypes as ChannelsActionType,
  Selectors as ChannelsSelectors,
  getChannelFromStorage,
} from '../epg/channels';

// returns a unique id for each license data request
function getContextKey(asset: Asset, streamId: number): string {
  return `${asset.id}/${streamId}`;
}

// ACTIONS
enum ActionType {
  REQUEST = 'playbackLicense/channel/REQUEST',
  PROCESSING = 'playbackLicense/channel/PROCESSING',
  SUCCESS = 'playbackLicense/channel/SUCCESS',
  FAIL = 'playbackLicense/channel/FAIL',
  DELETE = 'playbackLicense/channel/DELETE',
}

type MetaContextKey = {
  key: string;
};

type MetaPlaybackContext = {
  asset: Asset;
  streamId: number;
} & MetaContextKey;

type Request = ActionRequest<ActionType.REQUEST, MetaPlaybackContext>;
type Processing = ActionRequest<ActionType.PROCESSING, MetaContextKey>;
type Success = ActionSuccess<ActionType.SUCCESS, LinkEntry, MetaContextKey>;
type Fail = ActionFail<ActionType.FAIL, MetaContextKey>;
type Action = Processing | Request | Success | Fail;

const Actions = {
  request: (asset: Asset, streamId: number): Request => ({
    type: ActionType.REQUEST,
    meta: { asset, streamId, key: getContextKey(asset, streamId) },
  }),
  processing: (key: string): Processing => ({
    type: ActionType.PROCESSING,
    meta: { key },
  }),
  success: (key: string, payload: LinkEntry): Success => ({
    type: ActionType.SUCCESS,
    payload,
    meta: { key },
  }),
  fail: (key: string, error: XError): Fail => ({ type: ActionType.FAIL, error, meta: { key } }),
};

// SELECTORS

const Selectors = {
  isProcessing: (state: StoreState, key: string): boolean =>
    state.playbackLicense.channelsLicensedLinks[key]?.processing,
  error: (state: StoreState, key: string): XError | undefined =>
    state.playbackLicense.channelsLicensedLinks[key]?.error,
  link: (state: StoreState, key: string): LinkEntry | undefined =>
    state.playbackLicense.channelsLicensedLinks[key]?.data,
};

// REDUCER

type LicensedLink = {
  url: string;
  clearContent?: boolean;
};

type LinkEntry = {
  expirationDate: Time;
} & LicensedLink;

export type ReducerState = {
  [contextKey: string]: {
    processing: boolean;
    data?: LinkEntry;
    error?: XError;
  };
};

export default function licensedLinks(state: ReducerState = {}, action: Action): ReducerState {
  switch (action.type) {
    case ActionType.PROCESSING:
      return {
        ...state,
        [action.meta.key]: {
          processing: true,
        },
      };
    case ActionType.SUCCESS:
      return {
        ...state,
        [action.meta.key]: {
          data: action.payload,
          processing: false,
        },
      };
    case ActionType.FAIL:
      return {
        ...state,
        [action.meta.key]: {
          processing: false,
          error: action.error,
        },
      };
    default:
      return state;
  }
}

// SAGAS

function* isLicensedLinksCacheEnable(): SagaIterator<boolean> {
  const configs: Configs | undefined = yield select(Ducks.Dms.Selectors.getDmsConfigs);
  return (configs && configs.licensedLinkLifetime > 0) || false;
}

// request the license link from BE and saves it in the redux store
function* requestPlaybackDataSaga(action: Request): SagaIterator {
  // check if a request for the same context is already running
  const isProcessing = yield select(Selectors.isProcessing, action.meta.key);
  if (isProcessing) {
    return;
  }

  try {
    const result: PlaybackLicenseResult = yield Ducks.Api.callApi(
      PlaybackContextApi.getPlaybackData,
      action.meta.asset.id,
      PlaybackAssetType.MEDIA,
      action.meta.streamId
    );

    const configs: Configs = yield select(Ducks.Dms.Selectors.getDmsConfigs);

    put(
      Actions.success(action.meta.key, {
        url: result.data.url,
        clearContent: result.clearContent || false,
        expirationDate: TimeUtils.addHours(TimeUtils.now(), configs.licensedLinkLifetime),
      })
    );
  } catch (error) {
    put(Actions.fail(action.meta.key, XError.fromError(error)));
  }
}

function* prefetechLicensedLinksSaga(): SagaIterator {
  const cacheEnabled = yield call(isLicensedLinksCacheEnable);

  if (!cacheEnabled) {
    return;
  }

  const tunnedChannelEpgId: number | undefined = yield call(getChannelFromStorage);
  const channelIndex = yield select(ChannelsSelectors.getChannelIndexByEpgId, tunnedChannelEpgId);

  let start = channelIndex ? channelIndex - Constants.CHANNELS_LICENSE_OFFSET : 0;
  let end = channelIndex ? channelIndex + Constants.CHANNELS_LICENSE_OFFSET : Constants.CHANNELS_LICENSE_OFFSET * 2 - 1;

  const allChannels: Channel[] = yield select(ChannelsSelectors.getChannels);

  if (allChannels.length > Constants.CHANNELS_LICENSE_OFFSET * 2 + 1) {
    if (start < 0) {
      start = 0;
      end = Constants.CHANNELS_LICENSE_OFFSET * 2;
    } else if (end > allChannels.length - 1) {
      start = allChannels.length - 1 - Constants.CHANNELS_LICENSE_OFFSET * 2;
      end = allChannels.length - 1;
    }
  } else {
    start = 0;
    end = allChannels.length - 1;
  }

  let channel: Channel;
  let cacheKey: string;
  let cacheEntry: LinkEntry;
  for (let i = start; i <= end; i += 1) {
    channel = allChannels[i];

    // TODO: retrieve the best stream (if more then one available)

    // it's mandatory that every playable channel has at least one stream
    cacheKey = getContextKey(channel, channel.streams[0].id);
    cacheEntry = yield select(Selectors.link, cacheKey);

    if (!cacheEntry || TimeUtils.isBeforeNow(cacheEntry.expirationDate)) {
      put(Actions.request(channel, channel.streams[0].id));
    }
  }
}

export function* saga(): SagaIterator {
  yield takeEvery(ActionType.REQUEST, requestPlaybackDataSaga);
  // when the channel line up is loaded the prefecth will run
  yield takeLatest(ChannelsActionType.SUCCESS, prefetechLicensedLinksSaga);
  // TODO: call prefetechLicensedLinksSaga when a new channel is tuned
  // yield takeLatest(ChannelsActionType.SUCCESS, prefetechLicensedLinksSaga);
}

// API
export function* getLicensedLink(asset: Asset, streamId: number): SagaIterator<PlaybackLicenseResult> {
  const cacheKey = getContextKey(asset, streamId);

  const cachedLink: LinkEntry | undefined = yield select(Selectors.link, cacheKey);

  let url: string;
  let clearContent: boolean | undefined;

  if (cachedLink && TimeUtils.isAfterNow(cachedLink.expirationDate)) {
    url = cachedLink.url;
    clearContent = cachedLink.clearContent;
  } else {
    yield put(Actions.request(asset, streamId));

    const { success, fail }: { success: Success; fail: Fail } = yield race({
      success: take(ActionType.SUCCESS),
      fail: take(ActionType.FAIL),
    });

    if (fail) {
      throw fail.error;
    }

    url = success.payload.url;
    clearContent = success.payload.clearContent;
  }

  return {
    clearContent,
    data: {
      url,
    },
  };
}
